import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Avatar, Box, Button, Grid, Paper, Typography } from "@mui/material";
import axios from "axios";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { ToastContent } from "../../../shared/components";
import { useToast } from "../../../shared/hooks";
import { formatValueBR } from "../../../shared/utils/formatScaleValue";
import { usePixPolling } from "../hooks/usePixPolling";

export type PixInfo = {
	brcode: string;
};

export type BiometricsStatus = {
	status?: string | null;
};

type CreatePixFn = (id: string) => Promise<PixInfo>;
type StatusFetcherFn = (id: string) => Promise<BiometricsStatus>;

interface PixPaymentFormProps {
	value: number;
	active?: boolean;
	transactionId: string;
	createPix: CreatePixFn;
	statusFetcher: StatusFetcherFn;
	onPaid?: (payload: BiometricsStatus) => void;
	pollIntervalMs?: number;
	maxPollAttempts?: number;
}

export default function PixPaymentForm({
	value,
	active = true,
	transactionId,
	createPix,
	statusFetcher,
	onPaid,
	pollIntervalMs = 30_000,
	maxPollAttempts = 5,
}: PixPaymentFormProps) {
	const [loading, setLoading] = useState(false);
	const [pixInfo, setPixInfo] = useState<PixInfo | null>(null);
	const toast = useToast();

	const baseUrl = process.env.REACT_APP_BASE_API;

	useEffect(() => {
		if (!baseUrl) {
			toast.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Configuração inválida" content="REACT_APP_BASE_API não definido." />,
			});
		}
	}, []);

	// Geração do Pix (somente quando ativo e ainda não gerado)
	useEffect(() => {
		if (!active || pixInfo || !transactionId) return;

		let mounted = true;
		setLoading(true);

		createPix(transactionId)
			.then((data) => {
				if (!mounted) return;
				setPixInfo(data);
			})
			.catch(() => {
				toast.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content="Erro ao gerar o Pix. Tente novamente." />,
				});
			})
			.finally(() => mounted && setLoading(false));

		return () => {
			mounted = false;
		};
	}, [active, pixInfo, transactionId, createPix, toast]);

	// Polling do status — só inicia depois que o Pix existir
	usePixPolling<BiometricsStatus>({
		active: !!(active && pixInfo),
		transactionId,
		statusFetcher,
		intervalMs: pollIntervalMs,
		maxAttempts: maxPollAttempts,
		onPaid,
	});

	// URL da imagem do QR com cache-busting
	const qrSrc = useMemo(() => {
		if (!baseUrl || !transactionId) return "";
		const bust = Date.now(); // simples; se tiver updatedAt, use ele
		return `${baseUrl}pix/transaction/imagem/${transactionId}?v=${bust}`;
	}, [baseUrl, transactionId, pixInfo]);

	const handleCopy = async () => {
		if (!pixInfo?.brcode) return;
		try {
			await navigator.clipboard.writeText(pixInfo.brcode);
			toast.present({ type: "success", messageOrContent: "Código Pix copiado com sucesso!" });
		} catch {
			toast.present({ type: "error", messageOrContent: "Não foi possível copiar o código." });
		}
	};

	if (!pixInfo) {
		return (
			<Paper sx={{ p: 4, textAlign: "center" }} aria-live="polite">
				<Typography variant="body1">{loading ? "Gerando QR Code Pix..." : "Preparando pagamento..."}</Typography>
			</Paper>
		);
	}

	return (
		<Box sx={{ p: 2, bgcolor: "#fff" }}>
			<Box display="flex" flexDirection="column" mb={2} bgcolor="#f5f5f5" p={2} borderRadius={2} alignItems="center">
				<Typography variant="subtitle1" color="success.main" gutterBottom>
					Esse é o número do seu pedido:
				</Typography>
				<Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
					{transactionId}
				</Typography>
				<Typography variant="body2">
					<strong>Data e horário:</strong> {format(new Date(), "dd/MM/yyyy HH:mm")}
				</Typography>
			</Box>

			<Grid container spacing={4}>
				<Grid item xs={12} md={6} textAlign="center">
					<Typography variant="body1" gutterBottom>
						Valor a pagar:{" "}
						<Typography variant="h5" color="success.main" component="span">
							{formatValueBR(value)}
						</Typography>
					</Typography>

					<Typography variant="body2" gutterBottom>
						Escaneie o QR code:
					</Typography>

					<Box
						component="img"
						src={qrSrc}
						alt="QR Code para pagamento via Pix"
						sx={{ width: 256, height: 256, border: "1px solid #ccc", borderRadius: 2, p: 1, backgroundColor: "#fff", mt: 1 }}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<Box display="flex" flexDirection="column" gap={2}>
						<Box component="img" src="/PixLogo.svg" alt="Pix Logo" sx={{ width: 120 }} />
						{[
							"Acesse o app ou site do seu banco",
							"Busque a opção de pagar com Pix",
							"Leia o QR Code ou copie o código Pix",
							"Confirme os dados e finalize o pagamento",
							"Pronto! Você verá a confirmação do pagamento",
						].map((step, index) => (
							<Box display="flex" alignItems="center" gap={2} key={index}>
								<Avatar sx={{ bgcolor: "#e0e0e0", color: "#000", width: 32, height: 32, fontSize: "0.9rem", fontWeight: "bold" }}>
									{index + 1}
								</Avatar>
								<Typography variant="caption">{step}</Typography>
							</Box>
						))}
					</Box>
				</Grid>
			</Grid>

			<Box mt={2}>
				<Typography variant="body2" gutterBottom textAlign="center">
					Código válido por 1 dia
				</Typography>

				<Box
					sx={{
						wordBreak: "break-all",
						border: "1px dashed #ccc",
						borderRadius: 1,
						p: 2,
						bgcolor: "#f9f9f9",
						fontSize: "0.85rem",
						mb: 2,
						textAlign: "center",
					}}
				>
					{pixInfo.brcode}
				</Box>

				<Box maxWidth={300} mx="auto">
					<Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy} fullWidth disabled={!pixInfo?.brcode}>
						Copiar código
					</Button>
				</Box>
			</Box>
		</Box>
	);
}

// import ContentCopyIcon from "@mui/icons-material/ContentCopy";
// import { Avatar, Box, Button, Grid, Paper, Typography } from "@mui/material";
// import axios from "axios";
// import { format } from "date-fns";
// import { useEffect, useMemo, useState } from "react";
// import { ToastContent } from "../../../shared/components";
// import { useToast } from "../../../shared/hooks";
// import { formatValueBR } from "../../../shared/utils/formatScaleValue";

// interface PixPaymentFormProps {
// 	value: number;
// 	active?: boolean; // controla se a aba está visível
// }

// export default function PixPaymentForm({ value, active = true }: PixPaymentFormProps) {
// 	const [loading, setLoading] = useState(false);
// 	const [pixInfo, setPixInfo] = useState<any | null>(null);
// 	const toast = useToast();

// 	const transactionId = useMemo(() => {
// 		const pathParts = window.location.pathname.split("/");
// 		return pathParts[pathParts.length - 1];
// 	}, []);

// 	const baseUrl = process.env.REACT_APP_BASE_API;

// 	const generatePix = async () => {
// 		setLoading(true);
// 		try {
// 			const response = await axios.post(`${baseUrl}pix/transaction`, {
// 				transactionId,
// 			});
// 			if (response.status >= 200 && response.status < 300) {
// 				setPixInfo(response.data);
// 			} else {
// 				toast.present({
// 					type: "error",
// 					position: "top-right",
// 					messageOrContent: <ToastContent title="Erro" content="Erro inesperado ao gerar Pix." />,
// 				});
// 			}
// 		} catch (error) {
// 			console.error(error);
// 			toast.present({
// 				type: "error",
// 				position: "top-right",
// 				messageOrContent: <ToastContent title="Erro" content="Erro ao gerar Pix. Tente novamente." />,
// 			});
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	useEffect(() => {
// 		if (active && !pixInfo) {
// 			generatePix();
// 		}
// 	}, [active]);

// 	const handleCopy = async () => {
// 		try {
// 			await navigator.clipboard.writeText(pixInfo?.brcode);
// 			toast.present({
// 				type: "success",
// 				messageOrContent: "Código Pix copiado com sucesso!",
// 			});
// 		} catch {
// 			toast.present({
// 				type: "error",
// 				messageOrContent: "Não foi possível copiar o código.",
// 			});
// 		}
// 	};

// 	if (!pixInfo) {
// 		return (
// 			<Paper sx={{ p: 4, textAlign: "center" }}>
// 				<Typography variant="body1">{loading ? "Gerando QR Code Pix..." : "Preparando pagamento..."}</Typography>
// 			</Paper>
// 		);
// 	}

// 	return (
// 		<Box sx={{ p: 2, bgcolor: "#fff" }}>
// 			<Box display="flex" flexDirection="column" mb={2} bgcolor="#f5f5f5" p={2} borderRadius={2} alignItems="center">
// 				<Typography variant="subtitle1" color="success.main" gutterBottom>
// 					Esse é o número do seu pedido:
// 				</Typography>
// 				<Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
// 					{transactionId}
// 				</Typography>
// 				<Typography variant="body2">
// 					<strong>Data e horário:</strong> {format(new Date(), "dd/MM/yyyy HH:mm")}
// 				</Typography>
// 			</Box>

// 			<Grid container spacing={4}>
// 				<Grid item xs={12} md={6} textAlign="center">
// 					<Typography variant="body1" gutterBottom>
// 						Valor a pagar:{" "}
// 						<Typography variant="h5" color="success.main" component="span">
// 							{formatValueBR(value)}
// 						</Typography>
// 					</Typography>

// 					<Typography variant="body2" gutterBottom>
// 						Escaneie o QR code:
// 					</Typography>

// 					<Box
// 						component="img"
// 						src={`${baseUrl}pix/transaction/imagem/${transactionId}`}
// 						alt="QR Code Pix"
// 						sx={{
// 							width: 256,
// 							height: 256,
// 							border: "1px solid #ccc",
// 							borderRadius: 2,
// 							p: 1,
// 							backgroundColor: "#fff",
// 							mt: 1,
// 						}}
// 					/>
// 				</Grid>

// 				<Grid item xs={12} md={6}>
// 					<Box display="flex" flexDirection="column" gap={2}>
// 						<Box component="img" src="/PixLogo.svg" alt="Pix Logo" sx={{ width: 120 }} />
// 						{[
// 							"Acesse o app ou site do seu banco",
// 							"Busque a opção de pagar com Pix",
// 							"Leia o QR Code ou copie o código Pix",
// 							"Confirme os dados e finalize o pagamento",
// 							"Pronto! Você verá a confirmação do pagamento",
// 						].map((step, index) => (
// 							<Box display="flex" alignItems="center" gap={2} key={index}>
// 								<Avatar
// 									sx={{
// 										bgcolor: "#e0e0e0",
// 										color: "#000",
// 										width: 32,
// 										height: 32,
// 										fontSize: "0.9rem",
// 										fontWeight: "bold",
// 									}}
// 								>
// 									{index + 1}
// 								</Avatar>
// 								<Typography variant="caption">{step}</Typography>
// 							</Box>
// 						))}
// 					</Box>
// 				</Grid>
// 			</Grid>

// 			{/* Bloco de código Pix e botão Copiar fora do grid, 100% largura */}
// 			<Box mt={2}>
// 				<Typography variant="body2" gutterBottom textAlign="center">
// 					Código válido por 1 dia
// 				</Typography>

// 				<Box
// 					sx={{
// 						wordBreak: "break-all",
// 						border: "1px dashed #ccc",
// 						borderRadius: 1,
// 						p: 2,
// 						bgcolor: "#f9f9f9",
// 						fontSize: "0.85rem",
// 						mb: 2,
// 						textAlign: "center",
// 					}}
// 				>
// 					{pixInfo.brcode}
// 				</Box>

// 				<Box maxWidth={300} mx="auto">
// 					<Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy} fullWidth>
// 						Copiar código
// 					</Button>
// 				</Box>
// 			</Box>
// 		</Box>
// 	);
// }
